import React, { useState, useEffect } from 'react';
import { categorizeCoursesWithTenseMap } from './enhancedTenseMapCategories';
import './CourseQuestionAnalytics.css';

const CourseQuestionAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [coursesNeedingQuestions, setCoursesNeedingQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [minQuestions, setMinQuestions] = useState(10);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  useEffect(() => {
    fetchAnalytics();
  }, [minQuestions]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      // Fetch overall analytics
      const analyticsResponse = await fetch(`${API_URL}/course/analytics/question-counts`);
      if (!analyticsResponse.ok) throw new Error('Failed to fetch analytics');
      const analyticsData = await analyticsResponse.json();
      setAnalytics(analyticsData);

      // Fetch courses needing questions
      const needsResponse = await fetch(`${API_URL}/course/analytics/needs-questions?min=${minQuestions}`);
      if (!needsResponse.ok) throw new Error('Failed to fetch courses needing questions');
      const needsData = await needsResponse.json();
      setCoursesNeedingQuestions(needsData.courses_needing_questions || []);

    } catch (err) {
      setError(err.message);
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseDetails = async (courseId) => {
    try {
      const response = await fetch(`${API_URL}/course/analytics/${courseId}/questions`);
      if (!response.ok) throw new Error('Failed to fetch course details');
      const data = await response.json();
      setCourseDetails(data);
    } catch (err) {
      console.error('Error fetching course details:', err);
    }
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    fetchCourseDetails(course.course_id);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#EF4444'; // Red
      case 'medium': return '#F59E0B'; // Orange
      case 'low': return '#10B981'; // Green
      default: return '#6B7280'; // Gray
    }
  };

  const getTenseMapCategory = (courseName) => {
    if (!courseName) return null;
    const mockCourse = [{ course_item: courseName }];
    const { categories } = categorizeCoursesWithTenseMap(mockCourse);
    
    for (const [categoryKey, courses] of Object.entries(categories)) {
      if (courses.length > 0) {
        return {
          key: categoryKey,
          color: courses[0].tenseMapColor,
          description: courses[0].tenseMapDescription
        };
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Analyzing course question distribution...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="error-message">
          <h3>Error Loading Analytics</h3>
          <p>{error}</p>
          <button onClick={fetchAnalytics} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h2>Course Question Analytics</h2>
        <p>Identify courses that need more questions for optimal quiz functionality</p>
        
        <div className="controls">
          <label>
            Minimum Questions Required:
            <select 
              value={minQuestions} 
              onChange={(e) => setMinQuestions(parseInt(e.target.value))}
              className="min-questions-select"
            >
              <option value={5}>5 Questions</option>
              <option value={10}>10 Questions</option>
              <option value={15}>15 Questions</option>
              <option value={20}>20 Questions</option>
            </select>
          </label>
        </div>
      </div>

      {analytics && (
        <div className="summary-cards">
          <div className="summary-card">
            <h3>{analytics.summary.total_courses}</h3>
            <p>Total Courses</p>
          </div>
          <div className="summary-card urgent">
            <h3>{analytics.summary.courses_needing_questions}</h3>
            <p>Need Questions</p>
          </div>
          <div className="summary-card success">
            <h3>{analytics.summary.courses_with_sufficient_questions}</h3>
            <p>Sufficient Questions</p>
          </div>
          <div className="summary-card info">
            <h3>{analytics.summary.total_questions_needed}</h3>
            <p>Total Questions Needed</p>
          </div>
        </div>
      )}

      <div className="main-content">
        <div className="courses-list">
          <h3>Courses Needing Questions ({coursesNeedingQuestions.length})</h3>
          
          {coursesNeedingQuestions.length === 0 ? (
            <div className="no-courses">
              <p>🎉 All courses have sufficient questions!</p>
            </div>
          ) : (
            <div className="courses-grid">
              {coursesNeedingQuestions.map((course) => {
                const tenseCategory = getTenseMapCategory(course.course_item);
                return (
                  <div 
                    key={course.course_id}
                    className={`course-card ${selectedCourse?.course_id === course.course_id ? 'selected' : ''}`}
                    onClick={() => handleCourseSelect(course)}
                    style={{ borderLeftColor: getPriorityColor(course.priority) }}
                  >
                    <div className="course-header">
                      <h4>{course.course_item || course.course_title}</h4>
                      <span className={`priority-badge ${course.priority}`}>
                        {course.priority}
                      </span>
                    </div>
                    
                    <div className="course-stats">
                      <div className="stat">
                        <span className="stat-value">{course.verified_questions}</span>
                        <span className="stat-label">Verified</span>
                      </div>
                      <div className="stat">
                        <span className="stat-value">{course.total_questions}</span>
                        <span className="stat-label">Total</span>
                      </div>
                      <div className="stat urgent">
                        <span className="stat-value">{course.questions_needed}</span>
                        <span className="stat-label">Needed</span>
                      </div>
                    </div>

                    {tenseCategory && (
                      <div className="tense-category">
                        <div 
                          className="category-indicator"
                          style={{ backgroundColor: tenseCategory.color }}
                        ></div>
                        <span className="category-name">{tenseCategory.key.replace('_', ' ').toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selectedCourse && courseDetails && (
          <div className="course-details">
            <div className="details-header">
              <h3>{courseDetails.course_item}</h3>
              <button 
                onClick={() => setSelectedCourse(null)}
                className="close-details"
              >
                ×
              </button>
            </div>

            <div className="details-stats">
              <div className="detail-stat">
                <span>Verified Questions:</span>
                <span className="value">{courseDetails.verified_questions}</span>
              </div>
              <div className="detail-stat">
                <span>Pending Questions:</span>
                <span className="value">{courseDetails.pending_questions}</span>
              </div>
              <div className="detail-stat">
                <span>Questions Needed:</span>
                <span className="value urgent">{courseDetails.questions_needed}</span>
              </div>
            </div>

            <div className="action-buttons">
              <button 
                className="action-button primary"
                onClick={() => window.open(`/admin/questions/add?course=${courseDetails.course_id}`, '_blank')}
              >
                Add Questions
              </button>
              <button 
                className="action-button secondary"
                onClick={() => window.open(`/admin/questions?course=${courseDetails.course_id}`, '_blank')}
              >
                View Questions
              </button>
            </div>

            {courseDetails.questions && courseDetails.questions.length > 0 && (
              <div className="existing-questions">
                <h4>Existing Questions ({courseDetails.questions.length})</h4>
                <div className="questions-list">
                  {courseDetails.questions.map((question) => (
                    <div key={question.question_id} className="question-item">
                      <div className="question-text">{question.question_title}</div>
                      <div className="question-meta">
                        <span className={`status ${question.verified ? 'verified' : 'pending'}`}>
                          {question.verified ? 'Verified' : 'Pending'}
                        </span>
                        <span className="difficulty">
                          Difficulty: {question.difficulty || 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseQuestionAnalytics;
